import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import QuoteForm from '../../../components/feature/QuoteForm';

const RoofInspectionPage = () => {
  useEffect(() => {
    document.title = 'Free Roof Inspection Near Me | Commercial Roof Inspector | Fayetteville, Rogers, Bentonville';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Connect with professional roof inspectors in Northwest Arkansas. Free roof damage assessment, commercial roof inspector services, hail damage inspection for Fayetteville, Springdale, Rogers, Bentonville homes. Certified roof inspection services near me. Get free quotes today.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'free roof inspection near me, roof damage assessment, roof inspection NWA, commercial roof inspector, roof damage inspection, hail damage inspection, roof inspection services near me, Fayetteville roof inspection');
    }
  }, []);

  const faqs = [
    {
      question: 'How much does a roof inspection cost in Northwest Arkansas?',
      answer: 'Many contractors in our network provide completely free roof inspections throughout Northwest Arkansas. There is no cost, no obligation, and no hidden fees. Their certified inspectors will thoroughly assess your roof condition, document any damage with photos, and provide a detailed report at absolutely no charge. Whether you need a residential roof inspection or commercial roof inspector services, free inspections are available.'
    },
    {
      question: 'What does a professional roof inspection include?',
      answer: 'A comprehensive roof inspection includes examination of all shingles for damage or wear, flashing around chimneys and vents, gutters and downspouts, attic ventilation and insulation, signs of water damage or leaks, structural integrity assessment, and detailed photo documentation. You receive a complete written report with findings and recommendations. Commercial roof inspector services include additional structural assessments for larger properties.'
    },
    {
      question: 'How long does a roof inspection take?',
      answer: 'Most roof inspections take 45 minutes to 1 hour depending on roof size and complexity. Professional inspectors are thorough but efficient. They examine every aspect of your roof and provide immediate feedback on any concerns identified during the roof damage inspection.'
    },
    {
      question: 'When should I get my roof inspected?',
      answer: 'We recommend roof inspections after severe weather events like hail storms or high winds, annually as preventive maintenance, before buying or selling a home, if you notice leaks or water stains, when shingles appear damaged or missing, or if your roof is over 15 years old. Regular roof damage assessment catches problems early before they become expensive repairs. Commercial properties should schedule commercial roof inspector services at least annually.'
    },
    {
      question: 'Can contractors help with insurance claims after roof damage?',
      answer: 'Absolutely! Many contractors in our network specialize in insurance claim assistance. They document all damage thoroughly with photos and detailed reports, work directly with your insurance adjuster, provide accurate repair estimates, and advocate for fair compensation. Their experience with insurance claims and roof damage inspection ensures you receive the coverage you deserve.'
    },
    {
      question: 'Do contractors inspect commercial roofs as well?',
      answer: 'Yes, contractors in our network provide professional roof inspections for both residential and commercial properties throughout Northwest Arkansas. They are experienced commercial roof inspector professionals with all roof types including asphalt shingle, metal, tile, flat roofs, and TPO commercial roofing systems.'
    }
  ];

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-orange-600 to-orange-800">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://public.readdy.ai/ai/img_res/2e743558236f1a2623ccc6281403aae2.jpg"
            alt="Professional roof inspection Northwest Arkansas"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <div className="inline-block bg-green-500 text-white px-4 py-2 rounded-full font-semibold text-sm mb-4">
                100% FREE - NO OBLIGATION
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Connect with Free Roof Inspectors in NWA
              </h1>
              <p className="text-xl mb-8 leading-relaxed">
                Get matched with professional <strong>roof inspection services near me</strong> throughout Northwest Arkansas. Free <strong>roof damage assessment</strong> and <strong>commercial roof inspector</strong> services for Fayetteville, Springdale, Rogers, Bentonville, and Bella Vista. Certified inspectors identify storm damage, leaks, and wear before they become costly problems.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="tel:4795551234"
                  className="bg-white text-orange-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-orange-50 transition-all shadow-xl text-center whitespace-nowrap"
                >
                  Call (479) 555-1234
                </a>
                <a
                  href="#quote-form"
                  className="bg-gradient-to-r from-slate-700 to-slate-900 text-white px-8 py-4 rounded-lg font-bold text-lg hover:from-slate-800 hover:to-black transition-all shadow-xl text-center whitespace-nowrap"
                >
                  Get Free Inspection
                </a>
              </div>
            </div>
            <div className="hidden lg:block">
              <QuoteForm variant="compact" />
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Professional Roof Inspection Services in Northwest Arkansas
              </h2>
              
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                When searching for <strong>free roof inspection near me</strong> in Northwest Arkansas, homeowners need reliable connections to qualified professionals. We help you find certified <strong>roof inspection services near me</strong> with extensive experience identifying storm damage, leaks, and structural issues throughout Fayetteville, Springdale, Rogers, Bentonville, and surrounding communities. Our network provides comprehensive <strong>roof damage assessment</strong> services and <strong>commercial roof inspector</strong> expertise at absolutely no cost with no obligation to proceed with repairs.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Regular <strong>roof damage inspection</strong> is essential for maintaining your home's protection and value. Northwest Arkansas weather can be harsh on roofs, with severe hail storms, high winds, heavy rain, and extreme temperature fluctuations. These conditions cause gradual wear and sudden damage that may not be immediately visible from the ground. Professional <strong>roof inspection</strong> services catch problems early, saving you thousands in potential water damage and emergency repairs.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                The inspection process from contractors in our network is thorough and transparent. They examine every aspect of your roof, document findings with high-resolution photos, and provide detailed written reports. Whether you need a routine inspection, post-storm <strong>hail damage inspection</strong>, <strong>commercial roof inspector</strong> services, or pre-purchase evaluation, these professionals deliver accurate, honest assessments you can trust.
              </p>

              <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-8 mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  <i className="ri-gift-line text-green-600 mr-2"></i>
                  Why Roof Inspections Are Completely Free
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Many contractors in our network believe every homeowner deserves to know their roof's condition without financial barriers. Their <strong>free roof inspection</strong> service allows you to make informed decisions about repairs or replacement. They never pressure you to proceed with work—their goal is to provide honest <strong>roof damage assessment</strong> and expert recommendations.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Many homeowners discover their roof damage is covered by insurance. Free <strong>roof damage inspection</strong> includes documentation suitable for insurance claims, potentially saving you the entire cost of repairs or replacement. Even if you don't need immediate work, knowing your roof's condition provides peace of mind.
                </p>
              </div>

              <h3 className="text-3xl font-bold text-gray-900 mb-6">What's Included in a Comprehensive Roof Inspection</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-orange-50 rounded-lg p-6">
                  <i className="ri-home-line text-4xl text-orange-600 mb-3"></i>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">Exterior Inspection</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <i className="ri-checkbox-line text-green-500 mr-2 mt-1"></i>
                      <span>All shingles examined for damage, wear, or missing pieces</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-line text-green-500 mr-2 mt-1"></i>
                      <span>Flashing around chimneys, vents, and skylights</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-line text-green-500 mr-2 mt-1"></i>
                      <span>Gutters and downspouts for proper drainage</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-line text-green-500 mr-2 mt-1"></i>
                      <span>Ridge vents and roof ventilation systems</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-orange-50 rounded-lg p-6">
                  <i className="ri-building-line text-4xl text-orange-600 mb-3"></i>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">Interior Inspection</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <i className="ri-checkbox-line text-green-500 mr-2 mt-1"></i>
                      <span>Attic ventilation and insulation assessment</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-line text-green-500 mr-2 mt-1"></i>
                      <span>Signs of water intrusion or leaks</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-line text-green-500 mr-2 mt-1"></i>
                      <span>Structural integrity of roof decking</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-line text-green-500 mr-2 mt-1"></i>
                      <span>Moisture damage or mold indicators</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-orange-50 rounded-lg p-6">
                  <i className="ri-camera-line text-4xl text-orange-600 mb-3"></i>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">Photo Documentation</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <i className="ri-checkbox-line text-green-500 mr-2 mt-1"></i>
                      <span>High-resolution photos of all damage</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-line text-green-500 mr-2 mt-1"></i>
                      <span>Close-up images of problem areas</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-line text-green-500 mr-2 mt-1"></i>
                      <span>Overall roof condition documentation</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-line text-green-500 mr-2 mt-1"></i>
                      <span>Insurance-ready photo reports</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-orange-50 rounded-lg p-6">
                  <i className="ri-file-text-line text-4xl text-orange-600 mb-3"></i>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">Detailed Report</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <i className="ri-checkbox-line text-green-500 mr-2 mt-1"></i>
                      <span>Written assessment of roof condition</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-line text-green-500 mr-2 mt-1"></i>
                      <span>Identified issues and concerns</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-line text-green-500 mr-2 mt-1"></i>
                      <span>Repair or replacement recommendations</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-line text-green-500 mr-2 mt-1"></i>
                      <span>Estimated remaining roof lifespan</span>
                    </li>
                  </ul>
                </div>
              </div>

              <h3 className="text-3xl font-bold text-gray-900 mb-6">Common Roof Problems Inspectors Identify</h3>
              
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Experienced inspectors are trained to identify all types of roof damage and wear. During a <strong>roof damage assessment</strong>, they look for <strong>hail damage inspection</strong> findings that may not be visible from the ground, wind damage including lifted or missing shingles, storm damage to flashing and vents, aging and weathering of roofing materials, improper installation issues, inadequate ventilation causing premature wear, water damage and active leaks, structural problems with decking or supports, and granule loss on asphalt shingles.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                Many of these issues are not obvious to untrained eyes. Hail damage, for example, may appear as small bruises on shingles that compromise their integrity without creating immediate leaks. Professional <strong>roof damage inspection</strong> experts know exactly what to look for and can identify damage that insurance adjusters will recognize as legitimate claims. <strong>Commercial roof inspector</strong> services also identify unique issues in flat roofs, TPO systems, and large-scale commercial properties.
              </p>

              <div className="bg-orange-50 rounded-xl p-8 mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  <i className="ri-shield-check-line text-orange-600 mr-2"></i>
                  Insurance Claim Assistance Available
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If an inspection reveals storm damage, many contractors provide complete insurance claim assistance at no additional cost. Their detailed documentation meets insurance company requirements, and they work directly with adjusters to ensure fair compensation. They have helped thousands of NWA homeowners successfully navigate the claims process.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Many homeowners are surprised to learn their roof damage is covered by insurance. Even if you're unsure whether you have a claim, a free inspection will provide the information you need to make an informed decision about contacting your insurance company.
                </p>
              </div>

              <h3 className="text-3xl font-bold text-gray-900 mb-6">When to Schedule a Roof Inspection</h3>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <i className="ri-flashlight-line text-3xl text-orange-600 mr-4 mt-1"></i>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">After Severe Weather</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Schedule a <strong>roof damage inspection</strong> immediately after hail storms, high winds, or severe weather events. Even if you don't see obvious damage, hidden issues may exist that will worsen over time. Northwest Arkansas experiences frequent severe weather, making post-storm <strong>hail damage inspection</strong> essential.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <i className="ri-calendar-line text-3xl text-orange-600 mr-4 mt-1"></i>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Annual Preventive Maintenance</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Annual <strong>roof inspection services near me</strong> as preventive maintenance are recommended. Regular inspections catch minor issues before they become major problems, extending your roof's lifespan and preventing costly water damage to your home's interior. <strong>Commercial roof inspector</strong> services should be scheduled at least annually for business properties.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <i className="ri-home-heart-line text-3xl text-orange-600 mr-4 mt-1"></i>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Before Buying or Selling</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Get a professional <strong>roof damage assessment</strong> before purchasing a home to avoid unexpected repair costs. Sellers benefit from inspections that document roof condition, potentially increasing home value and speeding up sales.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <i className="ri-drop-line text-3xl text-orange-600 mr-4 mt-1"></i>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Signs of Damage or Leaks</h4>
                    <p className="text-gray-700 leading-relaxed">
                      If you notice water stains on ceilings, missing or damaged shingles, sagging areas, or increased energy bills, schedule a <strong>free roof inspection near me</strong> immediately. These signs indicate problems that require professional assessment and prompt repair.
                    </p>
                  </div>
                </div>
              </div>

              <h3 className="text-3xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h3>
              
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <details key={index} className="bg-gray-50 rounded-lg p-6 group">
                    <summary className="font-bold text-lg text-gray-900 cursor-pointer list-none flex items-center justify-between">
                      {faq.question}
                      <i className="ri-arrow-down-s-line text-2xl text-orange-600 group-open:rotate-180 transition-transform"></i>
                    </summary>
                    <p className="text-gray-700 leading-relaxed mt-4">{faq.answer}</p>
                  </details>
                ))}
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-8">
                <div id="quote-form">
                  <QuoteForm />
                </div>

                <div className="bg-gradient-to-br from-green-600 to-green-700 text-white rounded-xl p-8">
                  <h3 className="text-2xl font-bold mb-4">
                    <i className="ri-gift-line mr-2"></i>
                    Free Inspection Benefits
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-xl mr-2 mt-1"></i>
                      <span>100% free, no obligation</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-xl mr-2 mt-1"></i>
                      <span>Certified inspectors</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-xl mr-2 mt-1"></i>
                      <span>Detailed photo report</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-xl mr-2 mt-1"></i>
                      <span>Insurance documentation</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-xl mr-2 mt-1"></i>
                      <span>Expert recommendations</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-xl mr-2 mt-1"></i>
                      <span>Same-day scheduling</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gray-50 rounded-xl p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Related Services</h3>
                  <ul className="space-y-3">
                    <li>
                      <Link to="/services/storm-damage" className="text-orange-600 hover:text-orange-700 flex items-center">
                        <i className="ri-arrow-right-s-line mr-2"></i>
                        Storm Damage Repair
                      </Link>
                    </li>
                    <li>
                      <Link to="/services/insurance-adjuster" className="text-orange-600 hover:text-orange-700 flex items-center">
                        <i className="ri-arrow-right-s-line mr-2"></i>
                        Insurance Claims
                      </Link>
                    </li>
                    <li>
                      <Link to="/services/shingle-roofs" className="text-orange-600 hover:text-orange-700 flex items-center">
                        <i className="ri-arrow-right-s-line mr-2"></i>
                        Roof Replacement
                      </Link>
                    </li>
                    <li>
                      <Link to="/services/repairing-asphalt-roof" className="text-orange-600 hover:text-orange-700 flex items-center">
                        <i className="ri-arrow-right-s-line mr-2"></i>
                        Roof Repair
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-orange-600 to-orange-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            Get Your Free Roof Inspection Today
          </h2>
          <p className="text-xl mb-8 leading-relaxed">
            Don't wait for small problems to become expensive repairs. Connect with certified roof inspectors in Northwest Arkansas. Serving all of NWA with same-day scheduling available.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:4795551234"
              className="bg-white text-orange-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-orange-50 transition-all shadow-xl whitespace-nowrap"
            >
              Call (479) 555-1234
            </a>
            <Link
              to="/contact"
              className="bg-gradient-to-r from-slate-700 to-slate-900 text-white px-8 py-4 rounded-lg font-bold text-lg hover:from-slate-800 hover:to-black transition-all shadow-xl whitespace-nowrap"
            >
              Request Inspection Online
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RoofInspectionPage;
